<template>
  <div class="rule-form-step rule-form-step--start rule-form-step--start--step-1 flex max-w-4xl flex-col gap-5 py-5">
    <name-block
      :entity="entity"
      :description="tr('This will be the name of your currently created automation as well as the name of the campaign. The automation and campaign name will help you identify and easily manage the different automations you\'ve created.')"
      :disabled="$parent.readonlyMode"
    />

    <form-block
      :title="tr('Objective')"
      :description="tr('Here you can choose the specific goal that you want to achieve with your posts, such as increasing brand awareness, driving website traffic, promoting engagement with posts, etc.')"
    >
      <select-field
        v-model="entity.output_campaign.objective"
        :options="facebookCampaignObjectives"
        :reduce="(obj) => obj.machine_name"
        :placeholder="tr('Set an objective for the campaign')"
        :disabled="!entity.isNew"
      />
      <p class="m-0 text-sm text-muted-foreground">{{ tr('You will not be able to change this setting of this automation after the campaign has started.') }}</p>
    </form-block>

    <notifiable-users-block :entity="entity" :disabled="$parent.readonlyMode" />
    <activity-management-block :entity="entity" :disabled="$parent.readonlyMode" />

    <form-block :title="tr('AI Assist')">
      <label :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', $parent.readonlyMode ? 'cursor-not-allowed' : 'cursor-pointer' ]">
        <ui-checkbox
          :model-value="!!entity.settings.ai_review"
          :disabled="$parent.readonlyMode"
          @update:model-value="(value) => entity.settings.ai_review = value"
        />
        {{ tr('Checking for compliance with Meta Advertising Standards') }}
        <info-popup>
          <div v-html="tr('Before an advertisement is created from a post, the Infinite∞Ai reviews the textual and visual elements appearing on advertising platforms and prevents advertisements from being made from content that could violate the platform\'s advertising policies.')" />
        </info-popup>
      </label>
      <label :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', $parent.readonlyMode ? 'cursor-not-allowed' : 'cursor-pointer' ]">
        <ui-checkbox
          :model-value="!!entity.settings.dont_create_expired_ads"
          :disabled="$parent.readonlyMode"
          @update:model-value="(value) => entity.settings.dont_create_expired_ads = value"
        />
        {{ tr('Do not create ads from posts with expired content') }}
        <info-popup>
          <div v-html="tr('Infinite∞Ai reviews the content of your posts, and no ads are created for those that have lost their relevance.')" />
        </info-popup>
      </label>
      <label :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', ($parent.readonlyMode || !entity.isNew) ? 'cursor-not-allowed' : 'cursor-pointer' ]">
        <ui-checkbox
          :model-value="!!entity.settings.create_ai_stop_rule"
          :disabled="($parent.readonlyMode || !entity.isNew)"
          @update:model-value="(value) => entity.settings.create_ai_stop_rule = value"
        />
        {{ tr('Create Stop automation') }}
        <info-popup>
          <div v-html="tr('The Stop automation automatically stops advertisements whose content becomes outdated.')" />
        </info-popup>
      </label>
    </form-block>

    <brand-block :entity="entity" :disabled="$parent.readonlyMode || !entity.isNew" />

    <source-block :entity="entity" :disabled="$parent.readonlyMode || !entity.isNew" />

    <post-type-block :entity="entity" :disabled="$parent.readonlyMode" />

    <form-block
      :title="tr('Filters')"
      :description='tr("You can set different filters to determine the conditions under which the ad should start. You can add multiple conditions to an ad by clicking on the \"+\" sign, and you can connect them with \"and\" or \"or\" options.")'
    >
      <condition-group
        v-model="entity.conditions"
        :action="entity.action"
        :platforms="entity.selectedSourcePlatforms"
        :ad-account="entity.selectedAdAccount"
        :disable-post-metrics="entity.disablePostMetrics"
        :disabled="!filterIsAvailable || $parent.readonlyMode"
      />
    </form-block>

    <form-block
      :title="tr('Filtered items preview')"
    >
      <rule-preview :entity="entity" />
    </form-block>
  </div>
</template>

<script>
import RuleFormStepMixin from '@/mixins/RuleForm/Step.js';
import InfoPopup from '@/ui/components/InfoPopup.vue';
import ActivityManagementBlock from '@/ui/components/RuleForm/Blocks/ActivityManagementBlock.vue';
import BrandBlock from '@/ui/components/RuleForm/Blocks/BrandBlock.vue';
import NameBlock from '@/ui/components/RuleForm/Blocks/NameBlock.vue';
import NotifiableUsersBlock from '@/ui/components/RuleForm/Blocks/NotifiableUsersBlock.vue';
import PostTypeBlock from '@/ui/components/RuleForm/Blocks/PostTypeBlock.vue';
import SourceBlock from '@/ui/components/RuleForm/Blocks/SourceBlock.vue';
import ConditionGroup from '@/ui/components/RuleForm/Condition/ConditionGroup.vue';
import RulePreview from '@/ui/components/RulePreview.vue';
import SelectField from '@/ui/components/SelectField.vue';

export default {
  name: 'StartRuleFormStep1',
  components: {
    RulePreview,
    ActivityManagementBlock,
    BrandBlock,
    NameBlock,
    NotifiableUsersBlock,
    PostTypeBlock,
    SourceBlock,
    ConditionGroup,
    InfoPopup,
    SelectField,
  },
  mixins: [ RuleFormStepMixin ],
  computed: {
    facebookCampaignObjectives() {
      let conversions = [];

      if (this.isFbOdax) {
        conversions = this.fbOdaxMap.map(item => ({ label: item.label, machine_name: item.machine_name }));
      }
      else {
        conversions = this.$store.getters['app/facebookCampaignObjectives'];
      }

      return conversions;
    },
    hasSelectedSourceProfile() {
      return this.entity.source_profiles.filter(i => i.id).length !== 0;
    },
    filterIsAvailable() {
      return this.entity.hasSelectedAdAccount && this.hasSelectedSourceProfile;
    },
    facebookCampaignBidStrategies() {
      const list = this.$store.getters['app/facebookCampaignBidStrategies'];
      return this.conditionalFilteredList(list);
    },
  },
  watch: {
    'entity.ad_account.id'() {
      const settingFields = {
        'pixels': [
          'tracking_pixel_id',
          'adset_conversion_pixel_id',
        ],
        'advertisable_applications': [
          'adset_conversion_app_id',
          'tracking_advertisable_application_id',
        ],
      };

      for (const [ key, fields ] of Object.entries(settingFields)) {
        fields.forEach(field => {
          if (
            this.entity.settings[field]
            && !this.entity.selectedAdAccount[key].map(item => item.id).includes(this.entity.settings[field])
          ) {
            this.entity.settings[field] = this.entity.selectedAdAccount[key][0]?.id || null;
          }
        });
      }

      this.entity.settings.ad_content_product_catalogue_id = null;
      this.entity.settings.ad_content_product_set_id = null;
    },
    'entity.output_campaign.objective'(val, oldVal) {
      this.entity.settings.adset_conversion_location = null;
      this.entity.settings.adset_optimization_goal = null;
      this.entity.settings.adset_attribution_window = null;
      this.entity.settings.adset_conversion_pixel_id = null;
      this.entity.settings.adset_conversion_event = null;
      this.entity.settings.adset_conversion_app_id = null;
      this.entity.settings.ad_content_product_catalogue_id = null;
      this.entity.settings.ad_content_product_set_id = null;
      this.entity.settings.leadgen_form_id = null;

      if (val !== oldVal) {
        this.$store.commit('ruleForm/setAllowedStep', 1);
      }

      if (this.entity.settings.adset_conversion_app_store_url !== null) {
        this.entity.settings.adset_conversion_app_store_url = null;
        this.entity.settings.tracking_advertisable_application_id = null;
      }

      this.$nextTick(() => {
        const bidStrategy = this.entity.output_campaign.bid_strategy;
        if (
          bidStrategy !== null
          && !this.facebookCampaignBidStrategies.map(i => i.machine_name).includes(bidStrategy)
        ) {
          this.entity.output_campaign.bid_strategy = this.facebookCampaignBidStrategies[0].machine_name;
        }
      });
    },
    'entity.source_profiles': {
      handler() {
        this.entity.settings.leadgen_form_id = null;
      },
      deep: true,
    },
  },
  methods:{
    validate() {
      if (!this.validateBlocks()) {
        return false;
      }

      if (!this.entity.output_campaign.objective) {
        this.error(this.tr('Objective cannot be empty!'));
        return false;
      }

      if (!this.entity.isValidConditions) {
        this.error(this.tr('All Filter fields must be filled in!'));
        return false;
      }

      return true;
    },
  },
};
</script>
